/**
 * usePlanGate
 * Wraps an action handler with an instant client-side plan-feature check.
 * If the tenant's plan already includes the feature, the handler runs as-is.
 * Otherwise the global PlanLockDialog opens and the handler never runs.
 *
 * Wraps the handler function rather than a component so it works uniformly
 * across Button onClick, IconButton onClick, MenuItem onClick, and Switch
 * onChange — no component-shape gymnastics needed at each call site.
 */

import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/store';
import { showPlanLock } from '@/store/slices/planLockSlice';
import { FEATURE_DISPLAY_NAMES, usePlanFeature } from './usePlanFeature';
import { TenantFeatures } from '@/store/slices/authSlice';

export const usePlanGate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasFeature } = usePlanFeature();

  const guard = <A extends unknown[]>(
    featureKey: keyof TenantFeatures,
    fn: (...args: A) => void
  ) => {
    return (...args: A): void => {
      if (hasFeature(featureKey)) {
        fn(...args);
        return;
      }
      const label = FEATURE_DISPLAY_NAMES[featureKey];
      dispatch(
        showPlanLock({
          title: `${label} is locked`,
          message: `${label} is not available on your current plan. Upgrade to unlock this feature.`,
        })
      );
    };
  };

  return { guard, hasFeature };
};
